import React, { useCallback, useState } from 'react'

import { GetCard } from '@web/walter-lib/api'
import { SettingsTabs } from '../../types'
import General from './views/General/General'
import Sms from './views/Sms/Sms'
import Email from './views/Email/Email'
import Bio from './views/Bio/Bio'
import Approval from './views/Approval/Approval'
import About from './views/About/About'
import Question from './views/Question/Question'
import QuestionFlow from './views/QuestionFlow/QuestionFlow'
import Confirmation from '~components/ConfirmationModal/Confirmation'
import { usePromise } from '~hooks/usePromise'
import { cardsApi, useGetThreeDSConfigData, useGetThreeDSQuestionsData } from '~api'
import { processPermissionError } from '~utils/processPermissionError'

interface Props {
    onClose: () => void
    setTab: (tab: SettingsTabs) => void
    tab: SettingsTabs
    card?: GetCard
}

// eslint-disable-next-line complexity
function ThreeDS({ onClose, tab, setTab, card }: Props) {
    const [confirmModal, setConfirmModal] = useState({ showTwoFa: false, showVerificationCode: false })
    const { mutate: mutateConfig } = useGetThreeDSConfigData({ id: card?.id || '' })
    const { mutate: mutateQuestions } = useGetThreeDSQuestionsData({ id: card?.id || '' })

    const { send: onReset, isLoading: isResetLoading } = usePromise(async () => {
        try {
            await cardsApi.deleteThreeDSKba({
                id: card?.id || '',
            })
            await mutateQuestions()
            void mutateConfig()

            setTab(SettingsTabs.threeDSQuestionFlow)
        } catch (error) {
            const { showVerificationCode, showTwoFa } = processPermissionError(error)

            if (showVerificationCode || showTwoFa) {
                setConfirmModal({ showTwoFa, showVerificationCode })
                setTab(SettingsTabs.threeDSTwoFa)
                return
            }

            throw error
        }
    })

    const onSuccessConfirm = useCallback(() => {
        setTab(SettingsTabs.threeDSSecurity)
        void onReset()
    }, [onReset, setTab])

    const goBack = () => {
        setTab(SettingsTabs.threeDSGeneral)
    }

    return (
        <>
            {tab === 'threeDSGeneral' && <General setTab={setTab} card={card} />}
            {tab === 'threeDSSms' && <Sms onClose={goBack} />}
            {tab === 'threeDSEmail' && <Email onClose={goBack} />}
            {tab === 'threeDSBio' && <Bio onClose={goBack} />}
            {tab === 'threeDSApproval' && <Approval onClose={goBack} />}
            {tab === 'threeDSAbout' && <About onClose={goBack} />}
            {tab === 'threeDSSecurity' && (
                <Question
                    setTab={setTab}
                    onClose={onClose}
                    card={card}
                    onReset={onReset}
                    isResetLoading={isResetLoading}
                />
            )}
            {tab === 'threeDSQuestionFlow' && <QuestionFlow onClose={onClose} setTab={setTab} card={card} />}
            {tab === 'threeDSTwoFa' && (
                <Confirmation
                    initialInputState="pending"
                    onSuccess={onSuccessConfirm}
                    showTwoFa={confirmModal?.showTwoFa || false}
                    showVerificationCode={confirmModal?.showVerificationCode || false}
                    resource={cardsApi.resources.threeDSKba(card?.id || '')}
                />
            )}
        </>
    )
}

export default ThreeDS
